import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { readConsent } from "@/lib/analytics";
import { PHONE_HREF, calculatorHref } from "@/lib/landingLinks";

/**
 * A phone-only bar pinned to the bottom of a landing page.
 *
 * It waits until the cookie notice has been answered, because that notice sits
 * in the same corner of the screen - the visitor should never have to reach
 * past a sales button to accept or decline. The page also reserves matching
 * space at the end, so the bar never covers the last section's content.
 */
export const StickyMobileCta = ({ label, service, slug }) => {
  const { search } = useLocation();
  const [cookieAnswered, setCookieAnswered] = useState(() => Boolean(readConsent()));

  useEffect(() => {
    if (cookieAnswered) return undefined;
    const timer = window.setInterval(() => {
      if (readConsent()) setCookieAnswered(true);
    }, 500);
    return () => window.clearInterval(timer);
  }, [cookieAnswered]);

  // Reserve the bar's height at the foot of the page while it is showing.
  useEffect(() => {
    if (!cookieAnswered) return undefined;
    document.body.classList.add("tt-has-sticky-cta");
    return () => document.body.classList.remove("tt-has-sticky-cta");
  }, [cookieAnswered]);

  if (!cookieAnswered) return null;

  return (
    <div
      data-testid={`${slug}-sticky-cta`}
      className="tt-fade-in fixed inset-x-0 bottom-0 z-[200] border-t border-border bg-white/95 px-3 py-2.5 backdrop-blur-md sm:hidden"
    >
      <div className="flex items-center gap-2">
        <Button
          asChild
          data-testid={`${slug}-sticky-calculate`}
          className="h-12 min-h-[44px] flex-1 text-[15px] font-semibold"
        >
          <Link to={calculatorHref(service, search)}>{label}</Link>
        </Button>
        <Button
          asChild
          variant="outline"
          data-testid={`${slug}-sticky-call`}
          className="h-12 min-h-[44px] w-[88px] bg-white text-[15px] font-semibold"
        >
          <a href={PHONE_HREF} aria-label="Call T-TITE on 081 788 2993">
            <Phone className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Call
          </a>
        </Button>
      </div>
    </div>
  );
};
